// BL-2896: the one Sold / No Sale / Withdrawn tally. The auction close reports it and the
// Auction summary's Lot totals card shows it, so both call this (ADR 044 — Must Consolidate).
import { deriveTerminalDisposition } from './derive-lot-phase';
import type { LotTerminalInput } from './phase-tokens';

/** A lot's tally bucket. */
export type LotDispositionBucket = 'sold' | 'noSale' | 'withdrawn';

/** What the tally reads of a lot: its terminal stamps plus the stored clerk word. */
export type LotDispositionTallyInput = LotTerminalInput & { clerkDisposition: string | null };

/** The counts per bucket. */
export interface LotDispositionTally {
  sold: number;
  noSale: number;
  withdrawn: number;
}

/** The stored clerk word's bucket, read only when no decision stamp derives one. */
const CLERK_DISPOSITION_BUCKETS: Readonly<Record<string, LotDispositionBucket>> = {
  sold: 'sold',
  no_sale: 'noSale',
  withdrawn: 'withdrawn',
};

/**
 * Put a lot in exactly one bucket. The derived terminal disposition decides
 * (ADR 023 — Predicate Lifecycle); the stored clerk word counts only for a lot no
 * stamp resolves. BUG-2643: OR-ing the two per bucket counted a lot disposed No
 * Sale and then sold privately twice, because the private sale stamps
 * `hammered_at` and leaves `clerk_disposition = 'no_sale'`.
 *
 * @param lot - The lot's terminal stamps and clerk word.
 * @returns The lot's bucket, or null (a deleted lot, or one not yet resolved).
 */
export function lotDispositionBucket(lot: LotDispositionTallyInput): LotDispositionBucket | null {
  const terminal = deriveTerminalDisposition(lot);
  if (terminal != null) {
    if (terminal.kind === 'sold') return 'sold';
    if (terminal.kind === 'unsold') return 'noSale';
    if (terminal.kind === 'withdrawn') return 'withdrawn';
    return null;
  }
  return lot.clerkDisposition != null ? (CLERK_DISPOSITION_BUCKETS[lot.clerkDisposition] ?? null) : null;
}

/**
 * Count an auction's lots by bucket.
 *
 * @param lots - The auction's lots.
 * @returns Sold, No Sale and Withdrawn counts; a lot in no bucket is not counted.
 */
export function tallyLotDispositions(lots: readonly LotDispositionTallyInput[]): LotDispositionTally {
  const tally: LotDispositionTally = { sold: 0, noSale: 0, withdrawn: 0 };
  for (const lot of lots) {
    const bucket = lotDispositionBucket(lot);
    if (bucket != null) tally[bucket] += 1;
  }
  return tally;
}
